/**
 * Product Mapping API client — commercial product ↔ vendor product links
 * (cr001_product_mappingses).
 *
 * Used by the reconciliation worklist to optionally link a newly-created vendor
 * product to a commercial product so it feeds the billing/coverage control
 * (Control 2). Mirrors the pure-fetch service pattern.
 */

import type {
  ProductMapping,
  ProductMappingListResponse,
  CreateProductMappingRequest,
  UpdateProductMappingRequest,
} from '@techgitter/contracts';

const API_BASE_URL = '/api';

async function readError(response: Response, fallback: string): Promise<never> {
  const data = await response.json().catch(() => ({ error: `${fallback}: ${response.status}` }));
  throw new Error(data.error ?? `${fallback}: ${response.status}`);
}

export async function getProductMappings(
  params: { productId?: string; vendorProductId?: string; includeInactive?: boolean } = {},
  signal?: AbortSignal,
): Promise<ProductMapping[]> {
  const qs = new URLSearchParams();
  if (params.productId) qs.set('productId', params.productId);
  if (params.vendorProductId) qs.set('vendorProductId', params.vendorProductId);
  if (params.includeInactive) qs.set('includeInactive', 'true');
  const query = qs.toString();
  const response = await fetch(`${API_BASE_URL}/product-mappings${query ? `?${query}` : ''}`, {
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!response.ok) await readError(response, 'Failed to fetch product mappings');
  const data: ProductMappingListResponse = await response.json();
  return data.items ?? [];
}

export async function createProductMapping(
  payload: CreateProductMappingRequest,
  signal?: AbortSignal,
): Promise<ProductMapping> {
  const response = await fetch(`${API_BASE_URL}/product-mappings`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });
  if (!response.ok) await readError(response, 'Failed to create product mapping');
  return (await response.json()) as ProductMapping;
}

export async function updateProductMapping(
  id: string,
  payload: UpdateProductMappingRequest,
  signal?: AbortSignal,
): Promise<ProductMapping> {
  const response = await fetch(`${API_BASE_URL}/product-mappings/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });
  if (!response.ok) await readError(response, 'Failed to update product mapping');
  return (await response.json()) as ProductMapping;
}

export async function deleteProductMapping(id: string, signal?: AbortSignal): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/product-mappings/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!response.ok) await readError(response, 'Failed to delete product mapping');
}
